/* global globalThis */
import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../../utils/api';
import Select from 'react-select';
import './courses.css';
const safeGlobal = typeof globalThis !== 'undefined' ? globalThis : window;
const Courses = () => {
  const [departments, setDepartments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    course_level: '',
    playlist_id: '',
    category: '',
    department_name: ''
  });
  const [updateFormData, setUpdateFormData] = useState({
    course_id: '',
    title: '',
    description: '',
    playlist_id: '',
    course_level: '',
    category: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });
  const [updateIsSubmitting, setUpdateIsSubmitting] = useState(false);
  const [updateStatusMessage, setUpdateStatusMessage] = useState({ type: '', text: '' });

  // Validation function
  const isInvalidInput = (value) => {
    if (value.trim().length === 0) return true;
    const nonSpaceValue = value.replaceAll(/\s/g, '');
    if (nonSpaceValue.length === 0 || /^[^a-zA-Z0-9]+$/.test(nonSpaceValue)) return true;
    return false;
  };

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const data = await apiFetch(`${window._env_.API_BASE_URL}/admincourse/departments/`);
        setDepartments(data.departments || []);
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };

    const fetchCourses = async () => {
      try {
        const data = await apiFetch(`${window._env_.API_BASE_URL}/admincourse/courses/`);
        console.log('Courses data:', data);
        setCourses(data || []);
      } catch (error) {
        console.error('Error fetching courses:', error);
      }
    };

    fetchDepartments();
    fetchCourses();
  }, []);

  useEffect(() => {
    if (updateFormData.course_id) {
      const fetchCourseDetails = async () => {
        try {
          const courseDetails = await apiFetch(`${window._env_.API_BASE_URL}/admincourse/courses/${updateFormData.course_id}`);
          setUpdateFormData(prev => ({
            ...prev,
            title: courseDetails.title || '',
            description: courseDetails.description || '',
            playlist_id: courseDetails.playlist_id || '',
            course_level: courseDetails.course_level || '',
            category: courseDetails.category || ''
          }));
        } catch (error) {
          console.error('Error fetching course details:', error);
          // Fallback to finding in courses array if API fails
          const selectedCourse = courses.find(course => course.course_id === parseInt(updateFormData.course_id));
          if (selectedCourse) {
            setUpdateFormData(prev => ({
              ...prev,
              title: selectedCourse.title || '',
              description: selectedCourse.description || '',
              playlist_id: selectedCourse.playlist_id || '',
              course_level: selectedCourse.course_level || '',
              category: selectedCourse.category || ''
            }));
          }
        }
      };
      fetchCourseDetails();
    } else {
      setUpdateFormData(prev => ({
        ...prev,
        title: '',
        description: '',
        playlist_id: '',
        course_level: '',
        category: ''
      }));
    }
  }, [updateFormData.course_id, courses]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData(prevState => ({
      ...prevState,
      [name]: selectedOption ? selectedOption.value : ''
    }));
  };

  const handleUpdateChange = (e) => {
    const { name, value } = e.target;
    setUpdateFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleUpdateSelectChange = (selectedOption) => {
    setUpdateFormData(prevState => ({
      ...prevState,
      course_id: selectedOption ? selectedOption.value : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatusMessage({ type: '', text: '' });

    if (isInvalidInput(formData.title) || isInvalidInput(formData.playlist_id) || isInvalidInput(formData.category)) {
      setStatusMessage({ type: 'error', text: 'Title, Playlist ID, and Category cannot contain only spaces, special characters, or a combination of both.' });
      setIsSubmitting(false);
      return;
    }

    const selectedDept = departments.find(dept => dept.name === formData.department_name);
    if (!selectedDept) {
      setStatusMessage({ type: 'error', text: 'Please select a valid department.' });
      setIsSubmitting(false);
      return;
    }

    const payload = { ...formData, department_id: selectedDept.id, department_name: undefined };
    try {
      await apiFetch(`${window._env_.API_BASE_URL}/admincourse/courses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      setStatusMessage({ type: 'success', text: 'Course created successfully!' });
      // Reset form after successful submission
      setFormData({
        title: '',
        description: '',
        course_level: '',
        playlist_id: '',
        category: '',
        department_name: ''
      });
    } catch (error) {
      console.error('Error creating course:', error);
      setStatusMessage({ type: 'error', text: 'Failed to create course.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setUpdateIsSubmitting(true);
    setUpdateStatusMessage({ type: '', text: '' });

    if (isInvalidInput(updateFormData.playlist_id)) {
      setUpdateStatusMessage({ type: 'error', text: 'Playlist ID cannot contain only spaces, special characters, or a combination of both.' });
      setUpdateIsSubmitting(false);
      return;
    }

    if (!updateFormData.course_id) {
      setUpdateStatusMessage({ type: 'error', text: 'Please select a course.' });
      setUpdateIsSubmitting(false);
      return;
    }

    const payload = {
      title: updateFormData.title,
      description: updateFormData.description,
      playlist_id: updateFormData.playlist_id,
      course_level: updateFormData.course_level,
      category: updateFormData.category
    };

    try {
      await apiFetch(`${window._env_.API_BASE_URL}/admincourse/courses/${updateFormData.course_id}/playlist`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      setUpdateStatusMessage({ type: 'success', text: 'Course updated successfully!' });
      // Reset form
      setUpdateFormData({
        course_id: '',
        title: '',
        description: '',
        playlist_id: '',
        course_level: '',
        category: ''
      });
    } catch (error) {
      console.error('Error updating course:', error);
      setUpdateStatusMessage({ type: 'error', text: 'Failed to update course. ' + (error.message || '') });
    } finally {
      setUpdateIsSubmitting(false);
    }
  };

  // Options for react-select
  const courseLevelOptions = useMemo(() => [
    { value: 'Beginner', label: 'Beginner' },
    { value: 'Intermediate', label: 'Intermediate' },
    { value: 'Advanced', label: 'Advanced' }
  ], []);

  const departmentOptions = useMemo(() => departments.map(dept => ({
    value: dept.name,
    label: dept.name
  })), [departments]);

  const courseOptions = useMemo(() => courses.map(course => ({
    value: course.course_id,
    label: `${course.course_id} - ${course.title || `Course ${course.course_id}`}`
  })), [courses]);

  // Common styles for react-select
  const selectStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: '#fff',
      borderColor: '#ccc',
      color: '#000'
    }),
    option: (provided) => ({
      ...provided,
      color: '#000'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#000'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#000'
    })
  };

  return (
    <div className="courses-container">
      <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>Add New Course</h2>
      <form onSubmit={handleSubmit} className="courses-form">
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="Title"
          className="courses-input"
          required
        />
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Description (optional)"
          className="courses-input"
        />
        <Select
          name="course_level"
          value={courseLevelOptions.find(option => option.value === formData.course_level) || null}
          onChange={(option) => handleSelectChange(option, { name: 'course_level' })}
          options={courseLevelOptions}
          placeholder="Select Course Level"
          className="courses-select"
          classNamePrefix="select"
          isClearable
          isSearchable
          styles={selectStyles}
        />
        <input
          type="text"
          name="playlist_id"
          value={formData.playlist_id}
          onChange={handleChange}
          placeholder="Playlist or Video ID"
          className="courses-input"
          required
        />
        <input
          type="text"
          name="category"
          value={formData.category}
          onChange={handleChange}
          placeholder="Category"
          className="courses-input"
          required
        />
        <Select
          name="department_name"
          value={departmentOptions.find(option => option.value === formData.department_name) || null}
          onChange={(option) => handleSelectChange(option, { name: 'department_name' })}
          options={departmentOptions}
          placeholder="Select Department"
          className="courses-select"
          classNamePrefix="select"
          isClearable
          isSearchable
          styles={selectStyles}
        />
        <button
          type="submit"
          className="courses-button"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
        {statusMessage.text && (
          <div className={`status-message ${statusMessage.type}`}>
            {statusMessage.text}
          </div>
        )}
      </form>

      <h2 style={{ textAlign: 'center', marginBottom: '20px', marginTop: '40px' }}>Update Course</h2>
      <form onSubmit={handleUpdateSubmit} className="courses-form">
        <Select
          name="course_id"
          value={courseOptions.find(option => option.value === updateFormData.course_id) || null}
          onChange={handleUpdateSelectChange}
          options={courseOptions}
          placeholder="Select Course"
          className="courses-select"
          classNamePrefix="select"
          isClearable
          isSearchable
          styles={selectStyles}
        />
        <input
          type="text"
          name="title"
          value={updateFormData.title}
          onChange={handleUpdateChange}
          placeholder="Course Title"
          className="courses-input"
          required
        />
        <textarea
          name="description"
          value={updateFormData.description}
          onChange={handleUpdateChange}
          placeholder="Course Description (optional)"
          className="courses-input"
        />
        <input
          type="text"
          name="playlist_id"
          value={updateFormData.playlist_id}
          onChange={handleUpdateChange}
          placeholder="New Playlist or Video ID"
          className="courses-input"
          required
        />
        <Select
          name="course_level"
          value={courseLevelOptions.find(option => option.value === updateFormData.course_level) || null}
          onChange={(option) => {
            setUpdateFormData(prev => ({
              ...prev,
              course_level: option ? option.value : ''
            }));
          }}
          options={courseLevelOptions}
          placeholder="Select Course Level"
          className="courses-select"
          classNamePrefix="select"
          isClearable
          isSearchable
          styles={selectStyles}
        />
        <input
          type="text"
          name="category"
          value={updateFormData.category}
          onChange={handleUpdateChange}
          placeholder="Course Category"
          className="courses-input"
          required
        />
        <button
          type="submit"
          className="courses-button"
          disabled={updateIsSubmitting}
        >
          {updateIsSubmitting ? 'Submitting...' : 'Update Course'}
        </button>
        {updateStatusMessage.text && (
          <div className={`status-message ${updateStatusMessage.type}`}>
            {updateStatusMessage.text}
          </div>
        )}
      </form>
    </div>
  );
};

export default Courses;